/**
 * Lo que la sección iPhone le agrega a cada fila de la lista común.
 *
 * Una sola cosa: EN QUÉ COLORES VIENE.
 *
 * Un sellado no tiene un color como lo tiene un usado. El usado es una unidad
 * física —ese equipo es negro— y el sellado se pide: viene en los colores que
 * Apple hizo para ese modelo y el cliente elige. Por eso la ficha de un
 * sellado tiene `color` vacío y no es un olvido; escribir uno sería elegir por
 * el cliente.
 *
 * La pregunta que sí hay que contestar en el mostrador es «¿en qué colores
 * viene el 17 Pro?», y hasta ahora no estaba en ninguna parte del sistema.
 *
 * Cuando la ficha SÍ trae un color —o el nombre lo dice, como en «IPHONE 18
 * PRO MAX 256 GB BLACK»— entonces esa fila es ese color y se muestra uno solo:
 * ahí ya no se está ofreciendo el modelo, se está mostrando lo que hay.
 */
import type { MouseEvent } from "react";
import { Eye, EyeOff } from "lucide-react";

import type { ExtrasDeFila, GrupoDeFilas } from "@/components/catalogo/ListaProductos";
import { MuestraColor, MuestraColores } from "@/components/catalogo/MuestraColor";
import { UnidadesDelModelo } from "@/components/catalogo/UnidadesDelModelo";
import { colorDeIphone, coloresDelModelo } from "@/components/catalogo/colores";
import { IconButton } from "@/components/ui/icon-button";
import { aNumero, type Producto } from "@/lib/tipos";

import { FilasDeMemoria, MemoriasDelModelo, type GuardarPrecios } from "./MemoriasDelModelo";
import { modelosDesde, ordenarModelos } from "./modelos";
import { coloresDelRenglon, renglonesDeMemoria } from "./renglones";
import { agruparPorModelo, nombreDeModelo, parsearIphone, stockConUnidades } from "./logica";

/**
 * Lo que a un sellado le falta DE VERDAD.
 *
 * Ojo con lo que NO entra: una ficha sin color y en cero no está incompleta,
 * es la fila de lista de precios del modelo. Marcarla sería pedir que se
 * rompa la distinción que la sección acaba de establecer, y un cartel que
 * reclama lo que está bien deja de mirarse en dos días.
 *
 * Lo que sí falta es cualquiera de estas dos:
 *
 *   · UNIDADES SIN COLOR. Hay equipos en el local y la ficha no dice cuáles.
 *     Es la pregunta que llega por teléfono y no se puede contestar sin ir a
 *     abrir la caja.
 *   · SIN COSTO. Es una pantalla de administración: el costo al lado del
 *     precio es lo que deja ver el margen de un vistazo, y sin él la fila
 *     muestra medio dato. Además el precio de un producto sin costo no se
 *     recalcula solo cuando se mueve el dólar: queda congelado.
 *   · UN MODELO QUE NO SABEMOS DE QUÉ COLORES VIENE. La columna muestra «—» y
 *     el mostrador se queda sin la respuesta. No es un dato que falte cargar:
 *     es un lineup que falta agregar a `catalogo/colores.ts`.
 */
export function pendientesDeIphone(p: Producto): string[] {
  const faltan: string[] = [];
  if (aNumero(p.stock_actual) > 0 && !p.color) faltan.push("Falta el color");
  if (aNumero(p.costo) <= 0) faltan.push("Falta el costo");
  if (!p.color && coloresDelModelo(p.nombre).length === 0)
    faltan.push("No sabemos en qué colores viene");
  return faltan;
}

export interface ExtrasIphonesProps {
  /** Las unidades de cada modelo, por id del modelo. */
  unidades: ReadonlyMap<number, Producto[]>;
  onEditar: (p: Producto) => void;
}

export function extrasDeIphones({ unidades, onEditar }: ExtrasIphonesProps): ExtrasDeFila {
  return {
    pendientes: pendientesDeIphone,
    stock: (p) => stockConUnidades(p, unidades),

    /**
     * La fila es el modelo; el desplegable es su inventario. Una unidad suelta
     * —sin fila de modelo que la contenga— no despliega nada: ya es el equipo.
     */
    detalle: (p) => {
      if (p.color) return null;
      return (
        <UnidadesDelModelo
          unidades={unidades.get(p.id) ?? []}
          onEditar={onEditar}
          vacio="Todavía no entró ninguna unidad de este modelo. Se cargan con «Cargar iPhone», o entrando stock y eligiendo el color."
        />
      );
    },
    columnas: [
      {
        titulo: "Color",
        celda: (p) => {
          const propio = p.color ?? parsearIphone(p.nombre).detalle;
          if (colorDeIphone(propio)) return <MuestraColor color={propio} />;
          return <MuestraColores colores={coloresDelModelo(p.nombre)} />;
        },
      },
    ],
  };
}

/** Desde que generación se muestra el lineup completo. Del 13 para arriba es lo que rota. */
const GENERACION_MINIMA = 13;

// ─────────────────────────────────────────────────────────────────────────────
// Los modelos que no se trabajan, por sección
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las dos secciones que arman su lista con el lineup, y que por eso pueden
 * marcar modelos que el local no trabaja.
 */
export type SeccionDeModelos = "sellados" | "usados";

/**
 * Lo de Usados se guarda con prefijo; lo de Sellados, sin nada.
 *
 * Es una sola columna en la base para dos listas. Lo que ya estaba marcado
 * antes de que se separaran no tiene prefijo, y por eso sigue siendo de
 * Sellados: la sección donde se marcó.
 */
const PREFIJO_USADOS = "usados:";

/** Cómo se guarda «no trabajo este modelo» dicho desde esta sección. */
export function claveOculta(seccion: SeccionDeModelos, modelo: string): string {
  return seccion === "usados" ? `${PREFIJO_USADOS}${modelo}` : modelo;
}

/**
 * Cómo se guarda «no trabajo esta memoria» dicho desde esta sección.
 *
 * La clave de la memoria ya viene armada —"17 PRO MAX|512", la que escribe
 * `claveDeMemoria`— y acá solo se le antepone la sección, igual que con los
 * modelos: que no entren sellados de 1 TB no dice nada sobre los usados.
 */
export function claveMemoriaOculta(seccion: SeccionDeModelos, memoria: string): string {
  return seccion === "usados" ? `${PREFIJO_USADOS}${memoria}` : memoria;
}

/** De todo lo marcado, las memorias que le tocan a UNA sección. */
export function memoriasOcultasDe(
  guardados: ReadonlyArray<string>,
  seccion: SeccionDeModelos,
): Set<string> {
  return modelosOcultosDe(guardados, seccion);
}

/**
 * De todo lo marcado, los modelos que le tocan a UNA sección.
 *
 * Sellados y Usados son dos negocios distintos: que no entre ningún 16 Plus
 * nuevo no dice nada sobre si se toma usado, y al revés —hay modelos que ya no
 * vienen sellados y son justo los que más entran usados—. Antes la lista era
 * una sola y apagar un modelo en una solapa lo apagaba en la otra.
 */
export function modelosOcultosDe(
  guardados: ReadonlyArray<string>,
  seccion: SeccionDeModelos,
): Set<string> {
  const deUsados = seccion === "usados";
  return new Set(
    guardados
      .filter((m) => m.startsWith(PREFIJO_USADOS) === deUsados)
      .map((m) => (deUsados ? m.slice(PREFIJO_USADOS.length) : m)),
  );
}

/**
 * Las filas de la sección iPhone: el lineup de Apple con los productos encima.
 *
 * El esqueleto son los modelos que Apple hizo del 13 para arriba; encima van
 * las fichas cargadas, agrupadas por modelo. Un modelo sin fichas aparece
 * igual, diciendo que no está cargado y en qué colores viene: es un hueco del
 * catálogo, y hasta ahora no se veía en ningún lado —se descubría cuando
 * alguien llamaba a preguntar por un 16 Pro y no había qué contestarle—.
 *
 * Un modelo que NO está en el lineup pero sí en el catálogo (un Samsung, un
 * iPhone más viejo que todavía se vende) no se esconde: se agrega a la lista.
 * El lineup manda sobre lo que FALTA, nunca sobre lo que hay.
 *
 * La fila del modelo NO muestra plata. Mostraba un rango —«de tanto a tanto»—
 * que resumia lo que las memorias dicen exacto un renglon mas abajo, y encima
 * competia por la mirada con los numeros de verdad. El precio es de la
 * memoria: es lo que cambia cuando cambia la memoria.
 */
export function gruposDeModelos({
  filas,
  unidades,
  onEditar,
  conLineup = true,
  comoSeCarga,
  extras,
  ocultos,
  onAlternarOculto,
  memoriasOcultas,
  onAlternarMemoriaOculta,
  onGuardarPrecios,
  cotizacion,
  esModelo,
}: {
  filas: ReadonlyArray<Producto>;
  unidades: ReadonlyMap<number, Producto[]>;
  onEditar: (p: Producto) => void;
  /** Con que boton se carga el primero, para el modelo que todavia no esta. */
  comoSeCarga: string;
  /** Lo que la seccion le agrega a cada equipo, que ahora vive adentro. */
  extras?: ExtrasDeFila;
  /** Los modelos que este local NO trabaja. Van al fondo, apagados. */
  ocultos?: ReadonlySet<string>;
  onAlternarOculto?: (clave: string) => void;
  /** Las memorias que no se trabajan, ya con la clave "17 PRO MAX|512". */
  memoriasOcultas?: ReadonlySet<string>;
  onAlternarMemoriaOculta?: (clave: string) => void;
  /** Cargar costo y precio en el renglon, sin abrir la ficha. */
  onGuardarPrecios?: GuardarPrecios;
  cotizacion?: number;
  /** Si una ficha es de la lista de precios o un equipo que quedo suelto. */
  esModelo?: (p: Producto) => boolean;
  /**
   * Si se agregan los modelos del lineup que no estan cargados.
   *
   * Con un filtro puesto —«con stock», «ver solo los incompletos»— se apaga: la
   * persona dejo de mirar el catalogo y esta buscando algo puntual, y una fila
   * «sin cargar» no tiene stock ni datos incompletos que arreglar. Sumarla ahi
   * seria ruido justo cuando se pidio menos.
   */
  conLineup?: boolean;
}): GrupoDeFilas[] {
  const cargados = new Map(agruparPorModelo(filas).map((g) => [g.clave, g]));
  const claves = ordenarModelos([
    ...new Set([...(conLineup ? modelosDesde(GENERACION_MINIMA) : []), ...cargados.keys()]),
  ]);

  const filasDelLineup = claves.map((clave) => {
    const memorias = cargados.get(clave)?.memorias ?? [];
    /**
     * Los renglones del desplegable: el lineup de memorias con las fichas
     * encima. Se arma aca y no adentro del componente porque es la misma
     * decision que la fila —que el esqueleto sea lo que Apple hizo— y la
     * seccion es la que sabe de iPhones.
     */
    const renglones = renglonesDeMemoria({
      modelo: clave,
      memorias,
      unidades,
      ocultas: memoriasOcultas,
      conLineup,
      esModelo,
    });
    return {
      clave,
      titulo: nombreDeModelo(clave),
      stock: memorias.reduce((t, m) => t + stockConUnidades(m, unidades), 0),
      /**
       * La gama del modelo, en la columna COLOR. Es la respuesta a «¿en qué
       * colores viene?», que se contesta una vez por modelo: adentro, cada
       * memoria muestra los que de verdad hay.
       */
      columnas: [<MuestraColores colores={coloresDelModelo(`IPHONE ${clave} 128 GB`)} />],

      /** Lo que falta adentro: de las memorias y de los equipos que cuelgan. */
      pendientes: memorias.reduce(
        (total, m) =>
          total +
          (extras?.pendientes?.(m).length ?? 0) +
          (unidades.get(m.id) ?? []).reduce((t, e) => t + (extras?.pendientes?.(e).length ?? 0), 0),
        0,
      ),

      apagado: ocultos?.has(clave) ?? false,
      accion: onAlternarOculto ? (
        <IconButton
          icono={ocultos?.has(clave) ? Eye : EyeOff}
          tamano="sm"
          aria-label={
            ocultos?.has(clave)
              ? `Volver a trabajar ${nombreDeModelo(clave)}`
              : `No trabajo ${nombreDeModelo(clave)}`
          }
          title={
            ocultos?.has(clave)
              ? "Volver a trabajarlo: sube con los demás"
              : "No lo trabajo: baja al fondo, sin borrar nada"
          }
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onAlternarOculto(clave);
          }}
        />
      ) : null,
      /**
       * El desplegable, siempre con los renglones del lineup.
       *
       * Un modelo sin ninguna ficha ya no se contesta con un parrafo: se abre
       * y estan sus memorias, vacias y listas para cargarles la plata. El
       * parrafo queda solo para el modelo que ni siquiera tiene lineup —un
       * Samsung, un iPhone mas viejo que la tabla— donde no hay renglon que
       * ofrecer y el unico camino es el boton de alta.
       */
      /**
       * El desplegable de la TABLA: una fila de verdad por memoria, en las
       * mismas columnas que el modelo. La columna COLOR la contesta con los
       * colores que HAY de esa memoria —la gama del modelo ya esta arriba— y
       * las demas que tenga la seccion quedan vacias: una bateria es de un
       * equipo, no de un renglon de precios.
       */
      filas:
        renglones.length === 0
          ? undefined
          : (colSpan: number) => (
              <FilasDeMemoria
                renglones={renglones}
                unidades={unidades}
                onEditar={onEditar}
                extras={extras}
                onGuardarPrecios={onGuardarPrecios}
                onAlternarOculta={onAlternarMemoriaOculta}
                cotizacion={cotizacion}
                cantidadColumnas={extras?.columnas?.length ?? 0}
                columnas={(r) => [
                  coloresDelRenglon(r, unidades).map((c) => (
                    <MuestraColor key={c.id} color={c.color} />
                  )),
                ]}
                colSpan={colSpan}
              />
            ),

      /**
       * El desplegable del TELEFONO, que no tiene columnas que respetar. Y el
       * parrafo para el modelo que ni siquiera tiene lineup —un Samsung, un
       * iPhone mas viejo que la tabla—: ahi no hay renglon que ofrecer y el
       * unico camino es el boton de alta.
       */
      detalle:
        renglones.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Todavía no está cargado. {comoSeCarga} Recién con un precio se puede cotizar.
          </p>
        ) : (
          <MemoriasDelModelo
            renglones={renglones}
            unidades={unidades}
            onEditar={onEditar}
            extras={extras}
            onGuardarPrecios={onGuardarPrecios}
            onAlternarOculta={onAlternarMemoriaOculta}
            cotizacion={cotizacion}
          />
        ),
    };
  });

  /**
   * Los que no se trabajan van al fondo. No se sacan: sacarlos repetiria el
   * problema que el lineup vino a resolver —que lo que falta no se vea en
   * ningun lado— y ademas no habria de donde volver a prenderlos.
   */
  return [...filasDelLineup.filter((g) => !g.apagado), ...filasDelLineup.filter((g) => g.apagado)];
}
